import axios from 'axios';
import { getAuth, setAuth } from './identity.service';
import { AUTHENTICATE_USER } from './url.service';

// When a child store is active, storeToken (JWT_SECRET-signed) is in the AUTH cookie.
// resolve-principal checks Authorization: Bearer first, so it takes precedence over
// x-access-token and scopes all API calls to the child store's businessAccountId.
const buildAuthHeaders = (contentType = 'application/json') => {
  const auth = getAuth();
  const headers = {
    'x-access-token': auth?.accessToken,
    'x-platform': 'afto-web',
  };
  if (contentType) headers['content-type'] = contentType;
  if (auth?.storeToken) headers['Authorization'] = `Bearer ${auth.storeToken}`;
  return headers;
};

// AUTH (holding accessToken) is a plain client-set cookie, unlike NextAuth's
// own httpOnly session cookie — browsers increasingly cap or clear script-set
// cookies well before their declared expiry (Safari ITP, etc.), so a
// long-lived browser session can end up with a valid NextAuth session but a
// gone/stale AUTH cookie, 401ing every direct backend call. AUTHENTICATE_USER
// is the same no-auth, email-only endpoint NextAuth already calls to mint
// this token at login, so re-minting here needs no more privilege than
// knowing the session's own email. Concurrent 401s share one in-flight
// refresh instead of each minting their own token.
let refreshPromise = null;

const refreshAccessToken = () => {
  const auth = getAuth();
  const email = auth?.user?.email;
  if (!email) return Promise.resolve(null);

  if (!refreshPromise) {
    refreshPromise = axios
      .get(AUTHENTICATE_USER(email))
      .then((response) => {
        const freshToken = response?.data?.entity?.token;
        if (!freshToken) return null;
        setAuth({ ...auth, accessToken: freshToken });
        return freshToken;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

// Retries a single authenticated request once, after a silent token
// refresh, when it fails with 401 — see refreshAccessToken above. requestFn
// must build its own headers fresh on each call (via buildAuthHeaders) so
// the retry picks up the refreshed token rather than the stale one.
const withAuthRetry = (requestFn) => {
  return new Promise((resolve, reject) => {
    requestFn()
      .then((response) => {
        if (response && response.data) resolve(response.data);
      })
      .catch((ex) => {
        if (ex?.response?.status !== 401) {
          reject(ex);
          return;
        }
        refreshAccessToken().then((freshToken) => {
          if (!freshToken) {
            reject(ex);
            return;
          }
          requestFn()
            .then((response) => {
              if (response && response.data) resolve(response.data);
            })
            .catch((retryEx) => reject(retryEx));
        });
      });
  });
};

export const postWithAuth = (url, entity) =>
  withAuthRetry(() => axios.post(url, entity, { headers: buildAuthHeaders() }));

export const putWithAuth = (url, entity) =>
  withAuthRetry(() => axios.put(url, entity, { headers: buildAuthHeaders() }));

export const patchWithAuth = (url, entity) =>
  withAuthRetry(() => axios.patch(url, entity, { headers: buildAuthHeaders() }));

export const postWithOutAuth = (url, entity) => {
  return new Promise((resolve, reject) => {
    const headers = {
      'content-type': 'application/json',
      'x-platform': 'afto-web',
    };
    axios
      .post(url, entity, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((ex) => {
        reject(ex);
      });
  });
};

export const putWithOutAuth = (url, entity) => {
  return new Promise((resolve, reject) => {
    const config = {
      headers: {
        'Content-Type': entity.type,
      },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    };
    axios
      .put(url, entity, config)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.status);
        }
      })
      .catch((ex) => {
        reject(ex);
      });
  });
};

export const getWithAuth = (url) =>
  withAuthRetry(() => axios.get(url, { headers: buildAuthHeaders() }));

/**
 * A GET authorised by an explicit token instead of the AUTH cookie.
 *
 * The cookie is written by each page on its own schedule and keeps a child
 * store's storeToken across logins, so for a moment after signing in it can
 * still hold the previous account. A caller that must answer for exactly the
 * signed-in session passes that session's token here.
 *
 * No 401 retry: refreshing re-mints from the cookie's email, which would put
 * back the very dependency this exists to avoid. Callers treat a failure as
 * "unknown" and decide for themselves.
 */
export const getWithAccessToken = (url, accessToken) =>
  axios
    .get(url, { headers: { 'x-access-token': accessToken, 'x-platform': 'afto-web' } })
    .then((response) => response?.data);

export const getWithAuthParams = (url, params = {}) =>
  withAuthRetry(() => axios.get(url, { headers: buildAuthHeaders(), params }));

export const getWithOutAuth = (url) => {
  return new Promise((resolve, reject) => {
    const headers = {
      'content-type': 'application/json',
      'x-platform': 'afto-web',
    };
    axios
      .get(url, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((ex) => {
        reject(ex);
      });
  });
};

export const deleteWithAuth = (url) =>
  withAuthRetry(() => axios.delete(url, { headers: buildAuthHeaders() }));

export const deleteWithAuthParams = (url, params = {}) =>
  withAuthRetry(() => axios.delete(url, { headers: buildAuthHeaders(), params }));

export const deleteMultipleWithAuth = (url, entity) =>
  withAuthRetry(() => axios.delete(url, { headers: buildAuthHeaders(), data: entity }));

export const fetchStreamPostWithAuth = (url, entity, handleSuccess) => {
  return new Promise((resolve, reject) => {
    const headers = new Headers(buildAuthHeaders());

    fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(entity),
    })
      .then((response) => {
        if (response.ok) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          reader.read().then(function processText({ done, value }) {
            if (done) {
              return resolve();
            }
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            const parts = buffer.split('\n');
            parts.slice(0, -1).forEach((part) => {
              if (part.trim() !== '') {
                try {
                  const progressData = JSON.parse(part);
                  handleSuccess(progressData);
                } catch (error) {
                  reject(new Error('Failed to parse JSON'));
                }
              }
            });

            buffer = parts[parts.length - 1];
            reader.read().then(processText);
          });
        } else {
          reject(new Error('Failed to fetch service listing'));
        }
      })
      .catch((ex) => {
        reject(ex);
      });
  });
};

export const postWithAuthFormData = (url, formData) =>
  withAuthRetry(() => axios.post(url, formData, { headers: buildAuthHeaders(null) }));
